import { DataGridColumnType, FormManagementQuestionsQuery } from 'src/graphql';
import {
  API_TYPE_TO_UI_TYPE,
  QuestionData,
  QuestionOption,
} from 'src/types/formQuestionnaire';
import type { PreviewQuestionMinimal } from 'src/components/organisms/FormPreview/types';
import {
  mapApiOptionTranslations,
  mapApiQuestionTranslations,
} from 'src/components/pages/FormCreation/mappers/mapApiQuestionTranslations';

type ApiQuestion = NonNullable<
  FormManagementQuestionsQuery['formManagementQuestions']['questions']
>[number];

function mapApiOptionToQuestionOption(
  opt: NonNullable<ApiQuestion['options']>[number],
  questionType?: string,
  index?: number,
): QuestionOption {
  const optionText = opt.optionText ?? '';
  const supportsOtherOption =
    questionType === 'radio' || questionType === 'checkbox';
  const isOtherOption =
    supportsOtherOption && optionText === 'Other (optional)';

  const optionTranslations = mapApiOptionTranslations(opt.translations);

  return {
    id: opt.id ?? `option-${opt.optionOrder ?? index ?? 0}`,
    text: optionText,
    ...(optionTranslations?.length && { translations: optionTranslations }),
    ...(isOtherOption && {
      isOtherOption: true,
      helperText:
        'When the user selects "Other," a text field appears where they can enter their own answer.',
    }),
  };
}

function mapDataGridColumn(col: DataGridColumnType) {
  return {
    key: col.key ?? '',
    label: col.label ?? '',
    type: col.type ?? 'text',
    required: col.required ?? false,
    ...(col.maxLength != null && { maxLength: col.maxLength }),
    ...(col.options && { options: col.options }),
    ...(col.min != null && { min: col.min }),
    ...(col.max != null && { max: col.max }),
  };
}

function mapApiQuestionToQuestionData(
  apiQuestion: ApiQuestion,
  index: number,
): QuestionData {
  const typeKey = apiQuestion.questionType?.typeKey ?? '';
  const uiType = API_TYPE_TO_UI_TYPE[typeKey] ?? typeKey;

  const options = apiQuestion.options
    ?.slice()
    .sort((a, b) => (a.optionOrder ?? 0) - (b.optionOrder ?? 0))
    .map((opt, idx) => mapApiOptionToQuestionOption(opt, uiType, idx));

  const questionTranslations = mapApiQuestionTranslations(
    apiQuestion.translations,
  );

  const questionData: QuestionData = {
    id: apiQuestion.id ?? `question-${apiQuestion.orderIndex ?? index}`,
    questionText: apiQuestion.questionText ?? '',
    description: apiQuestion.description ?? '',
    questionType: uiType || undefined,
    isRequired: apiQuestion.isRequired ?? false,
    order: apiQuestion.orderIndex ?? index,
    displayNumber: index + 1,
    options: options && options.length > 0 ? options : undefined,
    ...(questionTranslations?.length && { translations: questionTranslations }),
  };

  if (uiType === 'date_picker' || uiType === 'time_picker') {
    questionData.dateTimeOptions = {
      datePicker: uiType === 'date_picker',
      timePicker: uiType === 'time_picker',
    };
  }

  if (uiType === 'database_field' && apiQuestion.databaseFieldSubTypeId) {
    questionData.databaseFieldOptions = {
      selectedFieldId: apiQuestion.databaseFieldSubTypeId,
      allowMultiSelect: apiQuestion.allowMultiSelect ?? false,
    };
  }

  if (apiQuestion.dataGridConfig) {
    const { columns, minRows, maxRows, addRowLabel, removeRowLabel } =
      apiQuestion.dataGridConfig;
    questionData.dataGridConfig = {
      columns: columns?.map(mapDataGridColumn) ?? [],
      ...(minRows != null && { minRows }),
      ...(maxRows != null && { maxRows }),
      ...(addRowLabel && { addRowLabel }),
      ...(removeRowLabel && { removeRowLabel }),
    };

    if (columns && columns.length > 0) {
      questionData.options = columns.map((col, idx) => {
        const columnTranslations = col.translations
          ?.filter((t) => t?.languageCode && t?.label)
          .map((t) => ({
            languageCode: t.languageCode!,
            optionText: t.label!,
          }));

        return {
          id: col.key ?? `col-${idx}`,
          text: col.label ?? '',
          nextAction: col.type,
          ...(columnTranslations?.length && {
            translations: columnTranslations,
          }),
        };
      });
    }
  }

  return questionData;
}

export function formDetailQuestionsToQuestionData(
  questions: ApiQuestion[],
): QuestionData[] {
  return questions
    .slice()
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map((q, i) => mapApiQuestionToQuestionData(q, i));
}

export function formDetailQuestionsToPreviewMinimal(
  questions: ApiQuestion[],
): PreviewQuestionMinimal[] {
  return questions
    .slice()
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map((q, i) => ({
      id: q.id ?? `q-${i}`,
      questionText: q.questionText ?? '',
      description: q.description ?? '',
      order: q.orderIndex ?? i,
      displayNumber: i + 1,
      isRequired: q.isRequired ?? false,
    }));
}
